import React, { useState, useEffect } from 'react';
import {
  Receipt,
  Plus,
  Search,
  Building2,
  Calendar,
  Layers,
  CheckCircle2,
  ArrowRight,
  Filter,
  MessageSquare,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import { InvoiceItem, PhysicalCompletionCertificateItem } from '../types/index.ts';
import { WhatsAppShareModal, WhatsAppShareData } from '../components/common/WhatsAppShareModal.tsx';
import { calculateSalesInvoiceTax } from '../services/taxEngine.ts';

interface InvoicesPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const InvoicesPage: React.FC<InvoicesPageProps> = ({ onNavigate }) => {
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [pccList, setPccList] = useState<PhysicalCompletionCertificateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showAddModal, setShowAddModal] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  // WhatsApp Sharing State
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [shareData, setShareData] = useState<WhatsAppShareData | null>(null);

  const [form, setForm] = useState({
    projectId: '',
    branchId: '',
    poNumber: '',
    pccId: '',
    subtotal: '',
    taxPercent: '16', // Configurable 16% Sales GST for UBL
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    remarks: 'Branch renovation milestone billing (PCC verified)',
  });

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const [iRes, pRes, pccRes] = await Promise.all([
        fetch('/api/invoices'),
        fetch('/api/projects'),
        fetch('/api/pcc'),
      ]);

      if (iRes.ok) {
        const iData = await iRes.json();
        if (Array.isArray(iData)) setInvoices(iData);
      }
      if (pccRes.ok) {
        const pccData = await pccRes.json();
        if (Array.isArray(pccData)) setPccList(pccData);
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (Array.isArray(pData)) {
          setProjects(pData);
          if (pData.length > 0) {
            setForm((prev) => ({
              ...prev,
              projectId: String(pData[0].id),
              branchId: String(pData[0].branchId),
              poNumber: pData[0].poNumber || '',
              subtotal: String(pData[0].contractValue || '500000'),
            }));
          }
        }
      }
    } catch (err) {
      console.warn('Invoices fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setInvoiceError(null);
    try {
      const res = await fetch('/api/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setInvoiceError(data.error || 'Failed to create invoice');
        return;
      }
      setShowAddModal(false);
      fetchInvoices();
    } catch (err: any) {
      setInvoiceError(err.message);
    }
  };

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  const filteredInvoices = invoices.filter((inv) => {
    const matchSearch =
      (inv.invoiceNumber || '').toLowerCase().includes(search.toLowerCase()) ||
      (inv.branchName || '').toLowerCase().includes(search.toLowerCase()) ||
      (inv.branchCode || '').toLowerCase().includes(search.toLowerCase()) ||
      (inv.projectName || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'ALL' || inv.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // Calculate live tax preview
  const previewTax = calculateSalesInvoiceTax(form.subtotal || 0, Number(form.taxPercent || 16));

  // Check verified PCC for selected project
  const verifiedPccForSelected = pccList.find(
    (p) =>
      (String(p.projectId) === form.projectId || String(p.branchId) === form.branchId) &&
      (p.verificationStatus === 'VERIFIED' || p.status === 'VERIFIED')
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Individual Branch Invoices
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900 border border-blue-200">
              16% Sales GST
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Individual branch billing against verified Physical Completion Certificates (PCC). Can be consolidated for centralized UBL payment.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onNavigate('consolidated_invoices')}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition-colors"
          >
            <Layers className="w-4 h-4 text-slate-600" />
            <span>Consolidated Billing</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
          >
            <Plus className="w-4 h-4 text-amber-400" />
            <span>Create Invoice</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by invoice #, branch code or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-lg text-xs px-2.5 py-1.5 text-slate-700 font-medium"
          >
            <option value="ALL">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="APPROVED">Approved</option>
            <option value="PAID">Paid</option>
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Invoice #</th>
                <th className="py-3 px-4">UBL Branch</th>
                <th className="py-3 px-4">Project Reference</th>
                <th className="py-3 px-4">Verified PCC</th>
                <th className="py-3 px-4">Subtotal</th>
                <th className="py-3 px-4">16% Sales GST</th>
                <th className="py-3 px-4">Grand Total</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Dispatch</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredInvoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-3 px-4 font-bold text-blue-900">{inv.invoiceNumber}</td>
                  <td className="py-3 px-4">
                    <div className="font-semibold text-slate-900">{inv.branchName}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{inv.branchCode}</div>
                  </td>
                  <td className="py-3 px-4 text-slate-700">{inv.projectName || inv.projectNumber}</td>
                  <td className="py-3 px-4">
                    {inv.pccVerified || inv.pccNumber ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        <ShieldCheck className="w-3 h-3 text-emerald-700" />
                        {inv.pccNumber || 'Verified PCC'}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-[11px]">Legacy / Advance</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-700">{formatPKR(inv.subtotal)}</td>
                  <td className="py-3 px-4 font-semibold text-emerald-700">
                    {formatPKR(inv.taxAmount)} ({inv.taxPercent}%)
                  </td>
                  <td className="py-3 px-4 font-bold text-slate-900">{formatPKR(inv.grandTotal)}</td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        inv.status === 'PAID'
                          ? 'bg-emerald-100 text-emerald-800'
                          : inv.status === 'SUBMITTED'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {inv.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        setShareData({
                          title: `UBL Branch Invoice - ${inv.invoiceNumber}`,
                          docType: 'INVOICE',
                          referenceNumber: inv.invoiceNumber,
                          projectName: inv.projectName,
                          branchName: inv.branchName,
                          amount: formatPKR(inv.grandTotal),
                          date: inv.invoiceDate,
                          detailsSummary: `Invoice ${inv.invoiceNumber} for ${inv.branchName} (${inv.branchCode}). Subtotal: ${formatPKR(
                            inv.subtotal
                          )} + 16% GST: ${formatPKR(inv.taxAmount)} = Total ${formatPKR(inv.grandTotal)}. PCC: ${
                            inv.pccNumber || 'Verified'
                          }.`,
                        });
                        setWhatsAppModalOpen(true);
                      }}
                      className="text-xs font-bold text-[#075e54] hover:text-white flex items-center gap-1.5 bg-[#25D366]/15 hover:bg-[#25D366] px-2.5 py-1.5 rounded-lg transition-colors ml-auto"
                      title="Send invoice via WhatsApp"
                    >
                      <MessageSquare className="w-3.5 h-3.5 text-[#25D366] group-hover:text-white" />
                      <span>WhatsApp</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* CREATE INVOICE MODAL WITH PCC GATING CONTROL */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">Generate UBL Sales Invoice</h3>
                <p className="text-xs text-slate-500">16% Sales Tax on Services &amp; Mandatory PCC Gating</p>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            {invoiceError && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                <span>{invoiceError}</span>
              </div>
            )}

            <form onSubmit={handleCreate} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Target Project</label>
                <select
                  value={form.projectId}
                  onChange={(e) => {
                    const sel = projects.find((p) => String(p.id) === e.target.value);
                    setForm({
                      ...form,
                      projectId: e.target.value,
                      branchId: sel ? String(sel.branchId) : form.branchId,
                      poNumber: sel?.poNumber || form.poNumber,
                      subtotal: sel?.contractValue ? String(sel.contractValue) : form.subtotal,
                    });
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.projectNumber} - {p.projectName}
                    </option>
                  ))}
                </select>
              </div>

              {/* PCC VERIFICATION GATING BADGE */}
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-1">
                <div className="font-semibold text-slate-800 flex items-center justify-between">
                  <span>Physical Completion Certificate (PCC) Status:</span>
                  {verifiedPccForSelected ? (
                    <span className="text-emerald-700 font-bold flex items-center gap-1 text-[11px]">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      VERIFIED ({verifiedPccForSelected.pccNumber})
                    </span>
                  ) : (
                    <span className="text-rose-700 font-bold flex items-center gap-1 text-[11px]">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      PCC Verification Required
                    </span>
                  )}
                </div>
                {!verifiedPccForSelected ? (
                  <p className="text-[11px] text-rose-600">
                    Warning: UBL regulations require verified physical completion sign-off prior to invoice submission.
                  </p>
                ) : (
                  <p className="text-[11px] text-emerald-800">
                    Sign-off verified by {verifiedPccForSelected.verifiedBy || 'Project Manager'}. Clearance granted to issue invoice.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Subtotal Before Tax (PKR)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="e.g. 1000000"
                    value={form.subtotal}
                    onChange={(e) => setForm({ ...form, subtotal: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Sales Tax on Services Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.taxPercent}
                    onChange={(e) => setForm({ ...form, taxPercent: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold"
                    required
                  />
                </div>
              </div>

              {/* TAX ENGINE BREAKDOWN */}
              <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3 space-y-1 text-xs">
                <div className="font-bold text-blue-950 flex items-center justify-between">
                  <span>Engine Calculated Invoice Total:</span>
                  <span className="text-blue-800 font-semibold">{form.taxPercent}% Sales GST</span>
                </div>
                <div className="flex justify-between text-slate-700 pt-1">
                  <span>Taxable Basis:</span>
                  <span>{formatPKR(previewTax.subtotal)}</span>
                </div>
                <div className="flex justify-between text-slate-700">
                  <span>16% Sales GST:</span>
                  <span className="font-semibold text-emerald-700">{formatPKR(previewTax.gstAmount)}</span>
                </div>
                <div className="pt-2 border-t border-blue-200 flex justify-between font-bold text-blue-950 text-sm">
                  <span>Grand Total Claim:</span>
                  <span>{formatPKR(previewTax.grandTotal)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={form.invoiceDate}
                    onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={form.dueDate}
                    onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Remarks / Claim Description</label>
                <input
                  type="text"
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold shadow-2xs"
                >
                  Issue Invoice
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {shareData && (
        <WhatsAppShareModal
          isOpen={whatsAppModalOpen}
          onClose={() => setWhatsAppModalOpen(false)}
          data={shareData}
        />
      )}
    </div>
  );
};
